"use client";

import Image from "next/image";
import Link from "next/link";
import { CloudSun, Menu, Search } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import BreakingNewsTicker from "@/components/layout/BreakingNewsTicker";
import DarkModeToggle from "@/components/ui/DarkModeToggle";
import { categories, getCategoryByAnySlug, getCategorySlugForLocale } from "@/lib/content/categories";
import { useLocaleAlternates } from "@/components/layout/LocaleAlternatesContext";
import { formatLocalizedDate } from "@/lib/utils/date";
import { withLocalePath } from "@/lib/utils/localePath";
import { formatMessage, getMessages } from "@/lib/i18n/messages";
import type { AppLocale } from "@/lib/utils/i18n";

type HeaderProps = {
  locale: AppLocale;
  breakingItems: Array<{ id: string; slug: string; title: string; categorySlug: string }>;
  tickerEnabled?: boolean;
};

const localeItems = [
  { code: "en", label: "EN", flagSrc: "/icons/flags/gb.svg" },
  { code: "fr", label: "FR", flagSrc: "/icons/flags/fr.svg" },
  { code: "ar", label: "AR", flagSrc: "/icons/flags/ma.svg" },
] as const;

const stripLocalePrefix = (path: string) => {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return normalized.replace(/^\/(fr|en)(?=\/|$)/, "") || "/";
};

const decodePathSegment = (segment: string) => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const Header = ({ locale, breakingItems, tickerEnabled = true }: HeaderProps) => {
  const messages = getMessages(locale);
  const articleAlternates = useLocaleAlternates();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [clickedPath, setClickedPath] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [stickyActivationScroll, setStickyActivationScroll] = useState<number | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [todayDate, setTodayDate] = useState(() => formatLocalizedDate(locale, new Date().toISOString()));
  const [stickyShellHeight, setStickyShellHeight] = useState(0);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  const stickyActivationAnchorRef = useRef<HTMLDivElement | null>(null);
  const stickyShellRef = useRef<HTMLDivElement | null>(null);

  const navItems = [
    { label: messages.header.nav.home, href: "/" },
    ...categories.map((category) => ({
      label: category.labels[locale],
      href: `/${getCategorySlugForLocale(category.slug, locale)}`,
    })),
  ];

  const homePath = withLocalePath(locale, "/");
  const searchPath = withLocalePath(locale, "/search");
  const currentPathWithQuery = searchParams.toString() ? `${pathname}?${searchParams.toString()}` : pathname;

  const getLocaleSwitchHref = (targetLocale: AppLocale) => {
    if (articleAlternates?.[targetLocale]) {
      return articleAlternates[targetLocale]!;
    }

    const normalizedPathWithQuery = currentPathWithQuery.startsWith("/")
      ? currentPathWithQuery
      : `/${currentPathWithQuery}`;
    const [rawPathname, rawQuery = ""] = normalizedPathWithQuery.split("?");
    const pathWithoutLocale = stripLocalePrefix(rawPathname);
    const segments = pathWithoutLocale
      .split("/")
      .filter(Boolean)
      .map((segment) => decodePathSegment(segment).trim());

    const firstSegmentCategory = segments.length > 0 ? getCategoryByAnySlug(segments[0]) : null;

    // An article path (/category/slug) needs the SLUG translated too, not just the category
    // segment -- each locale's article translation has its own distinct slug, so reusing the
    // current locale's slug under a different locale prefix produces a URL that 308-redirects
    // to the real one. articleAlternates (set client-side, post-hydration, by the article
    // page's own per-article lookup) has the correct value, but is never populated during SSR
    // -- so the raw server-rendered HTML that crawlers see would otherwise always carry this
    // wrong guessed URL. Route through /api/i18n/switch-locale instead, which does the real
    // per-locale slug lookup server-side and redirects straight to the correct URL.
    if (firstSegmentCategory && segments.length >= 2) {
      const switchParams = new URLSearchParams({ locale: targetLocale, path: normalizedPathWithQuery });
      return `/api/i18n/switch-locale?${switchParams.toString()}`;
    }

    if (firstSegmentCategory) {
      segments[0] = getCategorySlugForLocale(firstSegmentCategory.slug, targetLocale);
    }

    const translatedPath = segments.length > 0
      ? `/${segments.map((segment) => encodeURIComponent(segment)).join("/")}`
      : "/";
    const directPath = rawQuery ? `${translatedPath}?${rawQuery}` : translatedPath;
    return withLocalePath(targetLocale, directPath);
  };
  const routePath = pathname === homePath ? homePath : pathname;
  const activePath = clickedPath ?? routePath;

  useEffect(() => {
    setClickedPath(null);
    setIsMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!isMobileMenuOpen || !mobileMenuRef.current) {
        return;
      }

      if (!mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      if (stickyActivationScroll === null) {
        setIsScrolled(false);
        return;
      }

      setIsScrolled(window.scrollY >= Math.max(1, stickyActivationScroll));
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [stickyActivationScroll]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setTodayDate(formatLocalizedDate(locale, new Date().toISOString()));
    }, 60 * 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [locale]);

  useEffect(() => {
    const syncStickyActivation = () => {
      if (!stickyActivationAnchorRef.current) {
        return;
      }

      setStickyActivationScroll(Math.max(1, Math.floor(stickyActivationAnchorRef.current.offsetTop)));
    };

    syncStickyActivation();
    window.addEventListener("resize", syncStickyActivation);

    return () => {
      window.removeEventListener("resize", syncStickyActivation);
    };
  }, [tickerEnabled, isMobileMenuOpen, locale, pathname]);

  useEffect(() => {
    if (!stickyShellRef.current) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      setStickyShellHeight(Math.ceil(entry.contentRect.height));
    });

    observer.observe(stickyShellRef.current);

    return () => {
      observer.disconnect();
    };
  }, [tickerEnabled, isMobileMenuOpen, locale]);

  return (
    <>
      <header className="relative z-30">
        <div className="bg-[#1a1a1a] text-white">
          <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-[var(--page-padding)] py-1.5 text-[11px] min-[390px]:text-xs sm:py-2 sm:text-sm">
            <div className="flex min-w-0 items-center gap-2">
              <CloudSun className="h-4 w-4 rtl-icon" />
              <span className="truncate">{todayDate}</span>
            </div>

          </div>
        </div>
        <div className="border-b border-brand-border bg-white dark:bg-[#121212]">
          <div className="mx-auto w-full max-w-6xl px-[var(--page-padding)]">
            <div className="space-y-1.5 py-2 md:hidden">
              <div className="flex items-center justify-between gap-2">
                <span className="inline-flex h-[34px] w-[34px]" aria-hidden="true" />

                <Link href={homePath} className="relative h-9 w-28 shrink-0 min-[390px]:w-32">
                  <Image
                    src="/dark-logo.png"
                    alt={messages.header.logoAlt}
                    fill
                    sizes="(max-width: 389px) 112px, 128px"
                    quality={70}
                    className="object-contain dark:hidden"
                  />
                  <Image
                    src="/white-logo.png"
                    alt={messages.header.logoAlt}
                    fill
                    sizes="(max-width: 389px) 112px, 128px"
                    quality={70}
                    className="hidden object-contain dark:block"
                  />
                </Link>

                <DarkModeToggle />
              </div>

              <div dir="ltr" className="inline-flex w-full items-center justify-center gap-1 rounded-full border border-brand-border bg-brand-surface px-1 py-1">
                {localeItems.map((item) => {
                  const href = getLocaleSwitchHref(item.code);
                  const isActive = locale === item.code;

                  return (
                    <Link
                      key={item.code}
                      href={href}
                      className={`inline-flex flex-1 min-w-[70px] items-center justify-center gap-1 rounded-full px-2 py-1 text-center text-[11px] font-bold transition ${
                        isActive
                          ? "bg-brand-red text-white"
                          : "text-brand-muted hover:bg-brand-border/40 hover:text-brand-text"
                      }`}
                      aria-label={`${item.label} — ${formatMessage(messages.header.switchTo, {
                        locale: messages.locales[item.code],
                      })}`}
                    >
                      <Image
                        src={item.flagSrc}
                        alt=""
                        aria-hidden="true"
                        width={12}
                        height={8}
                        className="h-2 w-3 rounded-[2px] object-cover"
                      />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>

              <form
                action={searchPath}
                method="get"
                className="group flex items-center gap-2 rounded-xl border border-brand-border bg-brand-surface/95 px-2 py-1.5 shadow-sm transition-all duration-200 hover:border-brand-red/50 focus-within:border-brand-red focus-within:ring-2 focus-within:ring-brand-red/15"
              >
                <label htmlFor="news-search-bar-mobile" className="sr-only">
                  {messages.header.searchPlaceholder}
                </label>
                <Search className="h-4 w-4 rtl-icon text-brand-muted transition-colors group-focus-within:text-brand-red" />
                <input
                  id="news-search-bar-mobile"
                  type="search"
                  name="q"
                  placeholder={messages.header.searchPlaceholder}
                  className="w-full bg-transparent px-1 text-sm text-brand-text outline-none placeholder:text-brand-muted"
                />
                <button
                  type="submit"
                  aria-label={messages.header.searchButton}
                  className="inline-flex items-center gap-1 rounded-lg bg-brand-red px-2.5 py-1 text-xs font-bold text-white transition hover:brightness-110"
                >
                  <Search className="h-3.5 w-3.5 rtl-icon" />
                  {messages.header.searchButton}
                </button>
              </form>
            </div>

            <div className="hidden items-center gap-4 py-4 md:flex">
              <Link href={homePath} className="relative h-16 w-56 shrink-0">
                <Image
                  src="/dark-logo.png"
                  alt={messages.header.logoAlt}
                  fill
                  sizes="224px"
                  quality={70}
                  className="object-contain dark:hidden"
                />
                <Image
                  src="/white-logo.png"
                  alt={messages.header.logoAlt}
                  fill
                  sizes="224px"
                  quality={70}
                  className="hidden object-contain dark:block"
                />
              </Link>

              <div className="min-w-0 flex-1">
                <form
                  action={searchPath}
                  method="get"
                  className="group flex items-center gap-2 rounded-2xl border border-brand-border bg-brand-surface/95 px-2 py-1.5 shadow-sm transition-all duration-200 hover:border-brand-red/50 focus-within:border-brand-red focus-within:ring-2 focus-within:ring-brand-red/15"
                >
                  <label htmlFor="news-search-bar-desktop" className="sr-only">
                    {messages.header.searchPlaceholder}
                  </label>
                  <Search className="h-4 w-4 rtl-icon text-brand-muted transition-colors group-focus-within:text-brand-red" />
                  <input
                    id="news-search-bar-desktop"
                    type="search"
                    name="q"
                    placeholder={messages.header.searchPlaceholder}
                    className="w-full bg-transparent px-1 text-sm text-brand-text outline-none placeholder:text-brand-muted"
                  />
                  <button
                    type="submit"
                    aria-label={messages.header.searchButton}
                    className="inline-flex items-center gap-1 rounded-xl bg-brand-red px-3 py-1.5 text-xs font-bold text-white transition hover:brightness-110"
                  >
                    <Search className="h-3.5 w-3.5 rtl-icon" />
                    {messages.header.searchButton}
                  </button>
                </form>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <div dir="ltr" className="inline-flex items-center gap-1 rounded-full border border-brand-border bg-brand-surface px-1 py-1">
                  {localeItems.map((item) => {
                    const href = getLocaleSwitchHref(item.code);
                    const isActive = locale === item.code;

                    return (
                      <Link
                        key={item.code}
                        href={href}
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold transition ${
                          isActive
                            ? "bg-brand-red text-white"
                            : "text-brand-muted hover:bg-brand-border/40 hover:text-brand-text"
                        }`}
                        aria-label={formatMessage(messages.header.switchTo, {
                          locale: messages.locales[item.code],
                        })}
                      >
                        <Image
                          src={item.flagSrc}
                          alt=""
                          aria-hidden="true"
                          width={14}
                          height={10}
                          className="h-2.5 w-3.5 rounded-[2px] object-cover"
                        />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
                <DarkModeToggle />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div ref={stickyActivationAnchorRef} aria-hidden="true" className="h-0" />

      {isScrolled && stickyShellHeight > 0 ? <div aria-hidden="true" style={{ height: stickyShellHeight }} /> : null}

      <div
        ref={stickyShellRef}
        className={`z-40 transition-all duration-300 ${
          isScrolled
            ? "fixed inset-x-0 top-0 shadow-[0_10px_26px_rgba(0,0,0,0.35)]"
            : "relative"
        }`}
      >
        <div className={`transition-all duration-300 ${isScrolled ? "bg-[#1a1a1a]/95 backdrop-blur-md" : "bg-[#1a1a1a]"}`}>
          <nav className="relative mx-auto w-full max-w-6xl px-[var(--page-padding)] text-sm text-white">
            <div
              className="relative py-1.5 md:hidden"
            >
              <div ref={mobileMenuRef} className="relative">
                <button
                  type="button"
                  aria-label={messages.header.menu}
                  aria-expanded={isMobileMenuOpen}
                  onClick={() => setIsMobileMenuOpen((current) => !current)}
                  className="inline-flex w-full items-center justify-between rounded-xl border border-white/10 bg-[#232323] px-3 py-1.5 text-xs font-bold text-white transition hover:bg-[#2e2e2e] min-[390px]:text-sm"
                >
                  <span>{messages.header.menu}</span>
                  <Menu className="h-4 w-4" />
                </button>

                {isMobileMenuOpen ? (
                  <div className="absolute inset-x-0 top-[calc(100%+0.4rem)] z-50 rounded-xl border border-white/10 bg-[#191919] p-2 shadow-[0_18px_32px_rgba(0,0,0,0.35)]">
                    <div className="grid grid-cols-2 gap-1.5">
                      {navItems.map((item) => {
                        const path = withLocalePath(locale, item.href);
                        const isActive = activePath === path;

                        return (
                          <Link
                            key={`mobile-${item.label}`}
                            href={path}
                            onClick={() => {
                              setClickedPath(path);
                              setIsMobileMenuOpen(false);
                            }}
                            className={`rounded-lg px-3 py-1.5 text-[13px] font-semibold transition min-[390px]:py-2 min-[390px]:text-sm ${
                              isActive ? "bg-brand-red text-white" : "text-white/90 hover:bg-white/10"
                            }`}
                          >
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div
              className="hidden items-center gap-2 overflow-x-auto py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:flex"
            >
              {navItems.map((item) => {
              const path = withLocalePath(locale, item.href);
              const isActive = activePath === path;

              return (
                <Link
                  key={item.label}
                  href={path}
                  data-nav-path={path}
                  onClick={() => setClickedPath(path)}
                  className={`shrink-0 rounded-full px-4 font-semibold transition-all duration-300 ${
                    isActive
                      ? "relative z-10 bg-brand-red text-white shadow-[0_6px_18px_rgba(211,47,47,0.35)]"
                      : "relative z-10 bg-transparent text-white hover:bg-[#232323]"
                  } py-2 text-sm`}
                >
                  {item.label}
                </Link>
              );
            })}
            </div>
          </nav>

          {tickerEnabled ? (
            <div className="transition-all duration-300">
              <BreakingNewsTicker locale={locale} compact={isScrolled} items={breakingItems} />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default Header;
